/* Анкета нового тренера (REG-3): имя, фамилия, город, согласие с условиями — в стиле
   profile.html. Сюда попадает каждый новый аккаунт сразу после входа по коду (Login.tsx
   там имени и согласия больше не спрашивает — незачем заново у уже вошедших). */
import { trpc } from '@tg/app-core';
import { useState } from 'react';
import type { Me } from '../lib/data';
import { Modal } from '../ui/Modal';
import { toast } from '../ui/Toast';

export function Onboarding({ me }: { me: Me }) {
  const utils = trpc.useUtils();
  const [f, setF] = useState({ first: me.first, last: me.last, city: me.city || '' });
  const [agree, setAgree] = useState(false);
  const [terms, setTerms] = useState(false);
  const save = trpc.trainer.updateProfile.useMutation({
    onSuccess: () => utils.trainer.me.invalidate(),
    onError: (e) => toast(e.message || 'Не сохранилось — попробуйте ещё раз'),
  });
  const ready = !!f.first.trim() && !!f.last.trim() && agree;
  const submit = () => {
    if (!ready || save.isPending) return;
    save.mutate({ first: f.first.trim(), last: f.last.trim(), city: f.city.trim() || null, termsAccepted: true });
  };
  return (
    <div className="auth onb">
      <div className="auth-brand">
        <div className="auth-lg">
          <span>Тренерграм</span>
        </div>
        <p>Последний шаг — и кабинет готов: клиенты, программы и тренировки в одном месте.</p>
      </div>
      <div className="auth-side">
        <form
          className="auth-card"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <h1>Расскажите о себе</h1>
          <p className="lead">Это увидят клиенты, когда откроют вашу ссылку-приглашение. Остальное можно дополнить в профиле.</p>
          <div className="formfld">
            <span className="k">Имя</span>
            <input className="inp" autoFocus autoComplete="given-name" value={f.first} onChange={(e) => setF({ ...f, first: e.target.value })} placeholder="Иван" />
          </div>
          <div className="formfld">
            <span className="k">Фамилия</span>
            <input className="inp" autoComplete="family-name" value={f.last} onChange={(e) => setF({ ...f, last: e.target.value })} placeholder="Петров" />
          </div>
          <div className="formfld">
            <span className="k">
              Город <s>необязательно</s>
            </span>
            <input className="inp" autoComplete="address-level2" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} placeholder="Москва" />
          </div>
          <label className={'onb-agree' + (agree ? ' on' : '')}>
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
            <span>
              Принимаю{' '}
              <button type="button" className="lnk" onClick={() => setTerms(true)}>
                условия соглашения
              </button>
            </span>
          </label>
          <button className="btn auth-go" disabled={!ready || save.isPending}>
            {save.isPending ? 'Сохраняем…' : 'Перейти в кабинет'}
          </button>
        </form>
      </div>
      {terms && (
        <Modal
          title="Условия пользовательского соглашения"
          wide
          onClose={() => setTerms(false)}
          foot={
            <button className="btn" onClick={() => setTerms(false)}>
              Понятно
            </button>
          }
        >
          {/* Черновик — не юридический текст, заменить на согласованный вариант. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 14.5, lineHeight: 1.55, color: 'var(--tx2)' }}>
            <p style={{ margin: 0, color: 'var(--tx3)', fontSize: 13 }}>
              Черновик — предварительный текст для ознакомления, не итоговая юридическая версия. Будет заменён согласованным вариантом.
            </p>
            <div>
              <b style={{ color: 'var(--ink)' }}>1. Общие положения</b>
              <p style={{ margin: '4px 0 0' }}>
                Настоящее соглашение регулирует использование сервиса «Тренерграм» (далее — Сервис) тренером. Регистрируясь и принимая условия, вы подтверждаете, что ознакомлены с ними
                и согласны их соблюдать.
              </p>
            </div>
            <div>
              <b style={{ color: 'var(--ink)' }}>2. Предмет соглашения</b>
              <p style={{ margin: '4px 0 0' }}>
                Сервис предоставляет тренеру программное обеспечение для ведения клиентов, составления тренировок, учёта результатов и общения с клиентами. Сервис не оказывает тренерских
                или медицинских услуг и не несёт ответственности за содержание тренировок, назначаемых тренером клиентам.
              </p>
            </div>
            <div>
              <b style={{ color: 'var(--ink)' }}>3. Аккаунт и доступ</b>
              <p style={{ margin: '4px 0 0' }}>
                Вход осуществляется по номеру телефона и коду подтверждения. Тренер несёт ответственность за сохранность доступа к своему аккаунту и за действия, совершённые под его
                учётной записью.
              </p>
            </div>
            <div>
              <b style={{ color: 'var(--ink)' }}>4. Клиенты тренера</b>
              <p style={{ margin: '4px 0 0' }}>
                Тренер самостоятельно приглашает клиентов по персональной ссылке, ведёт их тренировки и переписку. Отношения между тренером и его клиентами (в том числе по качеству
                тренировок и безопасности) находятся вне ответственности Сервиса.
              </p>
            </div>
            <div>
              <b style={{ color: 'var(--ink)' }}>5. Оплата</b>
              <p style={{ margin: '4px 0 0' }}>
                Оплата занятий клиентами происходит напрямую тренеру, минуя Сервис; Сервис лишь отображает срок и сумму, указанные тренером. Оплата подписки на сам Сервис — отдельно,
                по выбранному тарифу.
              </p>
            </div>
            <div>
              <b style={{ color: 'var(--ink)' }}>6. Персональные данные</b>
              <p style={{ margin: '4px 0 0' }}>
                Сервис обрабатывает номер телефона, имя и данные о тренировках тренера и его клиентов для работы функций Сервиса. Данные не передаются третьим лицам, кроме случаев,
                предусмотренных законом.
              </p>
            </div>
            <div>
              <b style={{ color: 'var(--ink)' }}>7. Ограничение ответственности</b>
              <p style={{ margin: '4px 0 0' }}>
                Сервис предоставляется «как есть». Мы стараемся обеспечить бесперебойную работу, но не гарантируем отсутствие сбоев и не несём ответственности за убытки, возникшие
                в результате использования или невозможности использования Сервиса.
              </p>
            </div>
            <div>
              <b style={{ color: 'var(--ink)' }}>8. Изменение условий</b>
              <p style={{ margin: '4px 0 0' }}>
                Условия могут обновляться; об существенных изменениях тренер будет уведомлён в Сервисе. Продолжение использования Сервиса означает согласие с обновлёнными условиями.
              </p>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
